import { Component, ElementRef, NgZone, OnInit, Renderer2, ViewChild } from '@angular/core';
import { IonMenu, ModalController, Platform, PopoverController } from '@ionic/angular/lazy';
import { SplashScreen } from '@capacitor/splash-screen';
import { UiService } from '@services/ui.service';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Router } from '@angular/router';
import { AppUpdateService } from '@services/app-update.service';
import { environment } from 'src/environments/environment';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { StatusBar, Style } from '@capacitor/status-bar';
import { isWeb } from '@app/core/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map } from 'rxjs/operators';
import { AuthProvider } from '@services/auth.providers';
import { CurrentUserService } from '@smartmoving/sm-shared-lib';
import { register } from 'swiper/element/bundle';
import { LocalDatastoreService } from '@app/core/local-datastore-service';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';

// https://ionicframework.com/docs/angular/slides
register();

@UntilDestroy()
@Component({
  selector: 'sm-root',
  templateUrl: 'app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  showSpinner: boolean;
  isUserLoggedIn: boolean;
  userName: string;
  @ViewChild('userMenu') userMenu: IonMenu;

  constructor(
      private readonly platform: Platform,
      private readonly uiService: UiService,
      private readonly zone: NgZone,
      private readonly router: Router,
      private readonly updater: AppUpdateService,
      private readonly authProvider: AuthProvider,
      private readonly currentUser: CurrentUserService,
      private readonly renderer: Renderer2,
      private readonly elementRef: ElementRef,
      private readonly modalCtrl: ModalController,
      private readonly localDatastore: LocalDatastoreService,
      private readonly popoverController: PopoverController
  ) {
  }

  async initializeApp() {
    await this.authProvider.initialize();
    this.platform.ready().then(async () => {
      if (!isWeb()) {
        // A newly applied bundle must confirm it started within the updater's appReadyTimeout, or it is rolled back.
        await CapacitorUpdater.notifyAppReady();
      }
      await this.checkTokenExpiry();
      if (!isWeb()) {
        try {
          await FirebaseCrashlytics.setEnabled({ enabled: environment.production });
        } catch (e) {
          console.error('Failed to set FirebaseCrashlytics enabled state', e);
        }
        await StatusBar.setStyle({ style: Style.Light });
      }
      await SplashScreen.hide();

      if (!isWeb()) {
        await this.updater.resolvePendingUpdate();
      }

      if (!environment.apiUrl.includes('localhost') && !environment.apiUrl.includes('ngrok')) {
        await this.updater.updateNow();
      }
    });

    App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
      await this.zone.run(async () => {
        // to take the path /foo/bar from https://domain.com/#/foo/bar
        const slug = ('/' + event.url.split('/').slice(3).join('/')).replace('/#', '');
        if (slug) {
          await this.router.navigateByUrl(slug);
        }
      });
    });

    App.addListener('resume', async () => {
      await this.checkTokenExpiry();
    });
  }

  async ngOnInit() {
    this.setupGainsight();
    this.userName = this.currentUser.instance.displayName;
    await this.initializeApp();

    this.uiService.showSpinner.pipe(untilDestroyed(this)).subscribe((state) => {
      this.showSpinner = state;
    });

    this.authProvider.user$.pipe(untilDestroyed(this)).pipe(map(user => !!user)).subscribe(x => {
      this.isUserLoggedIn = x;
      this.userName = this.currentUser.instance.displayName;
    });
  }

  async logout() {
    await this.dismissAllActiveModalsPopoversAndMenus();
    await this.router.navigate(['/login']);
    await this.authProvider.logout();
  }

  async checkTokenExpiry() {
    if (!this.currentUser.instance) {
      return;
    }

    // User's auth token is not valid anymore.
    if (new Date(this.currentUser.instance?.expiration) < new Date()) {
      await this.logout();
    }
  }

  async navigateToSchedule() {
    this.localDatastore.navigateToToday.emit();
    await this.router.navigate(['/schedule']);
  }

  private setupGainsight() {
    const script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.text = `
      // Gainsight PX
      (function (n, t, a, e, co) {
        var i = "aptrinsic";
        n[i] = n[i] || function () {
          (n[i].q = n[i].q || []).push(arguments)
        }, n[i].p = e;
        n[i].c = co;
        var r = t.createElement("script");
        r.async = !0, r.src = a + "?disableTracking=true&a=" + e;
        var c = t.getElementsByTagName("script")[0];
        c.parentNode.insertBefore(r, c)
      })(window, document, "https://web-sdk.aptrinsic.com/api/aptrinsic.js",
      "${environment.production ? 'AP-NJFSLX5QHIWL-2' : 'AP-NJFSLX5QHIWL-2-3'}");`;

    this.renderer.appendChild(this.elementRef.nativeElement.ownerDocument.body, script);
  }

  private async dismissAllActiveModalsPopoversAndMenus() {
    await this.userMenu?.close();
    const activeModal = await this.modalCtrl.getTop();
    const activePopover = await this.popoverController.getTop();
    if (activeModal || activePopover) {
      await activeModal?.dismiss();
      await activePopover?.dismiss();
      await this.dismissAllActiveModalsPopoversAndMenus();
    }
  }
}
